import { useState } from "react";
import {
  Files, Clock, Star, Share2, Archive, Trash2, FolderPlus, Folder, FolderOpen,
  ChevronRight, MoreVertical, Pencil, Copy, Tag, HardDrive,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { buildTree, fmtSize } from "./util";

const VIEWS = [
  { key: "all", label: "All Documents", icon: Files },
  { key: "recent", label: "Recent", icon: Clock },
  { key: "favorites", label: "Favorites", icon: Star },
  { key: "shared", label: "Shared", icon: Share2 },
  { key: "archived", label: "Archived", icon: Archive },
  { key: "trash", label: "Trash", icon: Trash2 },
];

// Flatten the tree into visible rows based on the expanded set (avoids recursive JSX).
function flatten(nodes, expanded, depth, out) {
  nodes.forEach((n) => {
    out.push({ node: n, depth });
    if (expanded.has(n.folder_id) && n.children.length) flatten(n.children, expanded, depth + 1, out);
  });
  return out;
}

function FolderRow({ node, depth, active, expanded, onSelect, onToggle, onCtx, onDropItem }) {
  const [dragOver, setDragOver] = useState(false);
  const isOpen = expanded.has(node.folder_id);
  const hasChildren = node.children.length > 0;

  return (
    <div
      draggable
      onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("application/x-fv-folder", node.folder_id); }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); onDropItem(e, node.folder_id); }}
      onClick={() => onSelect(node.folder_id)}
      data-testid={`folder-node-${node.folder_id}`}
      style={{ paddingLeft: 8 + depth * 14 }}
      className={`group flex items-center gap-1.5 pr-1.5 h-9 rounded-lg cursor-pointer transition-colors ${
        active ? "bg-[#D4AF37]/12 text-[#D4AF37]" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
      } ${dragOver ? "ring-1 ring-[#D4AF37]/60 bg-[#D4AF37]/10" : ""}`}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(node.folder_id); }}
        className={`w-4 h-4 shrink-0 flex items-center justify-center ${hasChildren ? "" : "invisible"}`}
        data-testid={`folder-toggle-${node.folder_id}`}
      >
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
      </button>
      {isOpen && hasChildren ? <FolderOpen className="w-4 h-4 shrink-0" /> : <Folder className="w-4 h-4 shrink-0" />}
      <span className="text-[13px] truncate flex-1">{node.name}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()} data-testid={`folder-menu-${node.folder_id}`}>
          <button className="w-6 h-6 rounded-md opacity-0 group-hover:opacity-100 hover:bg-white/10 flex items-center justify-center">
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[#1C1C1F] border-white/10 text-white" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => onCtx("new", node)} data-testid={`folder-new-sub-${node.folder_id}`} className="focus:bg-white/10 text-sm"><FolderPlus className="w-4 h-4 mr-2" /> New subfolder</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onCtx("rename", node)} data-testid={`folder-rename-${node.folder_id}`} className="focus:bg-white/10 text-sm"><Pencil className="w-4 h-4 mr-2" /> Rename</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onCtx("duplicate", node)} data-testid={`folder-duplicate-${node.folder_id}`} className="focus:bg-white/10 text-sm"><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem onClick={() => onCtx("delete", node)} data-testid={`folder-delete-${node.folder_id}`} className="focus:bg-red-500/15 text-red-400 text-sm"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function DocumentsSidebar({
  view, folders, selectedFolderId, meta, tags, activeTag,
  onSelectView, onSelectFolder, onSelectTag, onFolderCtx, onDropItem, onNewFolder,
}) {
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const rows = flatten(buildTree(folders), expanded, 0, []);
  const storage = meta?.storage;

  return (
    <aside className="w-60 shrink-0 hidden lg:flex flex-col gap-5" data-testid="documents-sidebar">
      <nav className="space-y-0.5">
        {VIEWS.map((v) => {
          const Icon = v.icon;
          const active = view === v.key;
          return (
            <button key={v.key} onClick={() => onSelectView(v.key)} data-testid={`view-${v.key}`}
              className={`w-full flex items-center gap-2.5 h-9 px-3 rounded-lg text-[13px] transition-colors ${
                active ? "bg-[#D4AF37]/12 text-[#D4AF37] font-medium" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
              }`}>
              <Icon className="w-4 h-4" /> {v.label}
            </button>
          );
        })}
      </nav>

      <div>
        <div className="flex items-center justify-between px-3 mb-1">
          <span className="text-[11px] uppercase tracking-wider text-[#52525B]">Folders</span>
          <button onClick={() => onNewFolder(null)} data-testid="new-root-folder-btn" title="New folder"
            className="w-6 h-6 rounded-md text-[#71717A] hover:text-[#D4AF37] hover:bg-white/5 flex items-center justify-center">
            <FolderPlus className="w-4 h-4" />
          </button>
        </div>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDropItem(e, null)}
          className="min-h-[8px] max-h-64 overflow-y-auto pr-1" data-testid="folder-tree">
          {rows.length === 0 ? (
            <p className="px-3 py-2 text-xs text-[#52525B]">No folders yet</p>
          ) : (
            rows.map(({ node, depth }) => (
              <FolderRow key={node.folder_id} node={node} depth={depth}
                active={view === "folder" && selectedFolderId === node.folder_id}
                expanded={expanded} onSelect={onSelectFolder} onToggle={toggle} onCtx={onFolderCtx} onDropItem={onDropItem} />
            ))
          )}
        </div>
      </div>

      {tags.length > 0 && (
        <div>
          <span className="text-[11px] uppercase tracking-wider text-[#52525B] px-3 flex items-center gap-1.5 mb-1.5"><Tag className="w-3 h-3" /> Tags</span>
          <div className="flex flex-wrap gap-1.5 px-2">
            {tags.map((t) => (
              <button key={t} onClick={() => onSelectTag(activeTag === t ? "" : t)} data-testid={`tag-chip-${t}`}
                className={`text-[11px] px-2 py-1 rounded-full transition-colors ${
                  activeTag === t ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/5 text-[#A1A1AA] hover:text-white"}`}>
                #{t}
              </button>
            ))}
          </div>
        </div>
      )}

      {storage && (
        <div className="mt-auto rounded-xl border border-white/8 bg-[#121214] p-3.5" data-testid="storage-meter">
          <div className="flex items-center gap-2 mb-2 text-[13px] text-white"><HardDrive className="w-4 h-4 text-[#D4AF37]" /> Storage
            <span className="ml-auto text-[10px] uppercase tracking-wide text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2 py-0.5">{meta.tier}</span>
          </div>
          <Progress value={storage.pct} className="h-1.5 bg-white/10" data-testid="storage-progress" />
          <div className="text-[11px] text-[#71717A] mt-1.5">{fmtSize(storage.used)} of {fmtSize(storage.limit)} used</div>
        </div>
      )}
    </aside>
  );
}
